// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import React, { memo, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import FullCalendar, {
  DayHeaderContentArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/react';
const sprintf = require('sprintf-js').sprintf;

import Calendar from 'presentations/views/components/molecules/Calendar';
import {
  spacing,
  mediaMax,
  borderRadius,
  fontSize,
  fontWeight,
  isMediaMax,
  zIndex,
} from 'presentations/views/styles';
import {
  currentLocale,
  currentTimeZone,
  DateTime,
  formatToTime,
  formatToTimeShort,
  getDateTime,
} from 'infras/datetime';
import {
  UnavailableInfomation,
  ViewerCalendarEventListItem,
} from 'presentations/api/schema/calendar_event';
import { CalendarUnavailableReasonType } from 'presentations/api/schema/calendar_event/enum';
import {
  black,
  colorPalette,
  gray02,
  gray04,
  gray06,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import {
  convertHexToRGBA,
  convertHexToRGBIncludeAlpha,
} from 'presentations/views/helpers/color';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { SharedGuestUserItem } from 'presentations/api/schema/user';
import Icon from 'presentations/views/components/atoms/Icon';
import { Transparency } from 'domains/models/event/transparency';
import CalendarUnavailableButton from '../../molecules/CalendarUnavailableButton';

interface Props {
  isLoading?: boolean;
  selectedStartDate: DateTime;
  selectedDatetime: DateTime | null;
  today: DateTime;
  allTimeLists: Array<Array<(any | null)[]>>;
  timeLists: Array<Array<(any | null)[]>>;
  timeZone: string;
  color: string;
  isLightColor: boolean;
  duration: number;
  intervalMinutes: number;
  viewerBlocks: ViewerCalendarEventListItem[];
  viewerTeamUser?: TeamUserListItem;
  isShowViewerCalendar: boolean;
  isRevealGuestUser: boolean;
  isHostDiagnosticMode?: boolean;
  onSelect?: any;
  onClickBlockTime?: Function;
  isAvailable: boolean;
  isFetching: boolean;
  startWeekday?: number;
  displayWeekdays?: number[];
  onClickCalendarUnavailableButton?: any;
}

const CREATING_EVENT_ID_PREFIX = 'creating-';

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);

  const locale = currentLocale();
  const viewerTimeZone = currentTimeZone();

  const calendarUnavailableButtonRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullCalendarRef = useRef<FullCalendar>(null);
  const fullCalendarApi = fullCalendarRef.current?.getApi();
  useEffect(() => {
    fullCalendarApi?.gotoDate(props.selectedStartDate.toJSDate());
  }, [props.selectedStartDate]);

  const isShowUnavailableButton = useMemo(() => {
    return !props.isLoading && !props.isFetching && !props.isAvailable;
  }, [props.isLoading, props.isFetching, props.isAvailable]);

  let slotMinTime = '00:00:00';
  let slotMaxTime = '24:00:00';
  let numberOfVisibleHours = 24;
  if (props.allTimeLists.length > 0 && props.allTimeLists[0].length > 0) {
    slotMinTime = `${sprintf('%02d', props.allTimeLists[0][0][0])}:${sprintf(
      '%02d',
      props.allTimeLists[0][0][1],
    )}:00`;
    const lastIndex = props.allTimeLists[0].length - 1;

    // allTimeListsから時間範囲を取得し、duration分拡張
    let additionalHours = props.duration / 60;
    const additionalMinutes = props.duration % 60;
    let visibleMinute =
      (props.allTimeLists[0][lastIndex][1] ?? 0) + additionalMinutes;
    if (visibleMinute >= 60) {
      visibleMinute -= 60;
      additionalHours += 1;
    }
    let visibleHour =
      (props.allTimeLists[0][lastIndex][0] ?? 24) + additionalHours;
    if (visibleHour >= 24) {
      visibleHour = 24;
      visibleMinute = 0;
    }

    slotMaxTime = `${sprintf('%02d', visibleHour)}:${sprintf(
      '%02d',
      visibleMinute,
    )}:00`;

    numberOfVisibleHours =
      (props.allTimeLists[0][lastIndex][0] ?? 24) -
      (props.allTimeLists[0][0][0] ?? 0) +
      1;
  }

  const events: EventInput[] = useMemo(() => {
    const newEvents: EventInput[] = [];

    // 予約可能な時間
    const selectedTimeString: string | null = props.selectedDatetime
      ? formatToTime(props.selectedDatetime)
      : null;
    props.timeLists.forEach((timeList, index) => {
      const date = props.selectedStartDate.plus({
        days: index,
      });
      let isSelectedDate: boolean = false;
      if (props.selectedDatetime) {
        isSelectedDate = date.hasSame(props.selectedDatetime, 'day');
      }

      for (const time of timeList) {
        const viewerEventName = props.isShowViewerCalendar
          ? time[2] || null
          : null;
        const isTransparentEvent = time[6] === Transparency.Transparent;
        if (viewerEventName && !isTransparentEvent) {
          continue;
        }

        const timeString = `${sprintf('%02d', time[0])}:${sprintf(
          '%02d',
          time[1],
        )}`;
        const startedAt = date.set({
          hour: typeof time[0] === 'number' ? time[0] : undefined,
          minute: typeof time[1] === 'number' ? time[1] : undefined,
          second: 0,
          millisecond: 0,
        });
        const endedAt = startedAt.plus({
          minute: props.duration,
        });
        let capacityText: string | null = null;
        let isFullCapacity = false;
        if (time.length >= 4 && time[3] !== null) {
          if (Number(time[3]) > 0) {
            capacityText = t('remaining capacity', {
              count: time[3],
            });
          } else {
            isFullCapacity = true;
            capacityText = t('full capacity');
          }
        }
        const isSelectedDateTime =
          isSelectedDate && timeString === selectedTimeString;
        const guestUsers: SharedGuestUserItem[] | null =
          props.isRevealGuestUser && time && time.length >= 5 ? time[4] : null;

        // block理由周りをitemから取得する
        const unavailableInfomations: UnavailableInfomation[] =
          time && time.length >= 6 ? time[5] : [];
        const uniqueUnavailableInfomations: UnavailableInfomation[] = [];
        if (unavailableInfomations) {
          for (const reason of unavailableInfomations) {
            if (
              uniqueUnavailableInfomations.find(
                (uui) => uui.reason === reason.reason,
              )
            ) {
              continue;
            }
            uniqueUnavailableInfomations.push(reason);
          }
        }
        if (viewerEventName && isTransparentEvent) {
          uniqueUnavailableInfomations.push({
            reason: CalendarUnavailableReasonType.Transparent,
            users: [],
            eventName: viewerEventName,
          });
        }
        const hasUnavailableInfomation =
          uniqueUnavailableInfomations.length > 0;

        newEvents.push({
          title: `${formatToTimeShort(startedAt, props.timeZone)}`,
          start: startedAt.toJSDate(),
          end: endedAt.toJSDate(),
          allDay: false,
          className:
            'selectable-event' +
            (isFullCapacity ? ' full-event' : ' ') +
            (isSelectedDateTime ? ' selected-event' : ' ') +
            (props.isHostDiagnosticMode && hasUnavailableInfomation
              ? isTransparentEvent
                ? ' transparent-event'
                : ' block-event'
              : ' '),
          id: `${CREATING_EVENT_ID_PREFIX}${newEvents.length}`,
          extendedProps: {
            timeString,
            date,
            capacityText,
            guestUsers,
            reasons:
              props.isHostDiagnosticMode && hasUnavailableInfomation
                ? uniqueUnavailableInfomations
                : undefined,
          },
        });
      }
    });

    // 予定
    const calendarOffset =
      getDateTime(new Date(), props.timeZone).offset -
      getDateTime(new Date(), viewerTimeZone).offset;
    props.viewerBlocks.forEach((block: ViewerCalendarEventListItem) => {
      const color = props.viewerTeamUser?.color || colorPalette.primary.www;
      const startedAt = getDateTime(block.startedAt, props.timeZone).plus({
        minutes: calendarOffset,
      });
      const endedAt = getDateTime(block.endedAt, props.timeZone).plus({
        minutes: calendarOffset,
      });
      const isShortEvent = endedAt.diff(startedAt, 'hour').hours < 1;
      newEvents.push({
        title: block.name || t('No Event Name'),
        start: startedAt.toJSDate(),
        end: endedAt.toJSDate(),
        allDay: block.isAllDay,
        borderColor: convertHexToRGBA(color, 80),
        backgroundColor: convertHexToRGBIncludeAlpha(color, 8),
        className: `${isShortEvent ? ' short-event' : ' '}`,
      });
    });

    return newEvents;
  }, [
    props.timeLists,
    props.selectedStartDate,
    viewerTimeZone,
    props.timeZone,
    props.duration,
    props.intervalMinutes,
    props.viewerBlocks,
    props.isHostDiagnosticMode,
  ]);

  const eventClick = (args: EventClickArg) => {
    if (args.event.id.includes(CREATING_EVENT_ID_PREFIX)) {
      const {
        timeString,
        date,
        guestUsers,
        reasons,
      } = args.event.extendedProps;
      if (Array.isArray(reasons)) {
        props.onClickBlockTime && props.onClickBlockTime(timeString, reasons);
      } else {
        props.onSelect(timeString, date, guestUsers);
      }
    }
  };

  const dayHeaderContent = (args: DayHeaderContentArg) => {
    return (
      <>
        <WeekDayHeaderDate
          today={args.isToday}
          color={props.color}
          isLightColor={props.isLightColor}
        >
          {args.date.getDate()}
        </WeekDayHeaderDate>
        <WeekDayHeaderWeekday>
          {DateTime.fromJSDate(args.date, { locale: locale }).toFormat('EEE')}
        </WeekDayHeaderWeekday>
      </>
    );
  };

  const unavailableTimeListHeight = isMediaMax('tablet') ? 500 : 400;
  let unavailableButtonLeftPos = 0;
  let unavailableButtonTopPos = 0;
  if (containerRef.current && calendarUnavailableButtonRef.current) {
    unavailableButtonLeftPos =
      (containerRef.current.offsetWidth -
        calendarUnavailableButtonRef.current.offsetWidth) /
      2;

    unavailableButtonTopPos =
      (unavailableTimeListHeight -
        calendarUnavailableButtonRef.current.offsetHeight) /
      2;
  }

  const isTablet = isMediaMax('tablet');

  return (
    <TimeList
      ref={containerRef}
      color={props.color}
      numberOfVisibleHours={props.isLoading ? 6.5 : numberOfVisibleHours}
      isLightColor={props.isLightColor}
      isShowUnavailableButton={isShowUnavailableButton}
      unavailableTimeListHeight={unavailableTimeListHeight}
    >
      {props.isLoading ? (
        <>
          <LoadingHeader>
            <LoadingAxis />
            {Array.from({ length: 7 }).map((v, i) => (
              <LoadingCell key={i}>
                <WeekDayHeaderDate
                  today={false}
                  color={props.color}
                  isLightColor={props.isLightColor}
                >
                  <Skeleton
                    width={isTablet ? 20 : 40}
                    height={isTablet ? 20 : 40}
                  />
                </WeekDayHeaderDate>
                <WeekDayHeaderWeekday>
                  <Skeleton width={isTablet ? 20 : 40} height={20} />
                </WeekDayHeaderWeekday>
              </LoadingCell>
            ))}
          </LoadingHeader>
          <LoadingAllday>
            <LoadingAxis>
              <Skeleton />
            </LoadingAxis>
            {Array.from({ length: 7 }).map((v, i) => (
              <LoadingCell showBorderRight={i !== 6} key={i}></LoadingCell>
            ))}
          </LoadingAllday>
          {Array.from({ length: 5 }).map((v, i) => (
            <LoadingBody hideBorderBottom={i === 5} key={i}>
              <LoadingAxis />
              {Array.from({ length: 7 }).map((v, j) => (
                <LoadingCell showBorderRight={j !== 6} key={j}>
                  <Skeleton width="100%" height="50%" />
                  <Skeleton width="100%" height="50%" />
                </LoadingCell>
              ))}
            </LoadingBody>
          ))}
        </>
      ) : (
        <Calendar
          // 全体設定
          locale={locale}
          timeZone={viewerTimeZone}
          fullCalendarRef={fullCalendarRef}
          initialView={'timeGridWeek'}
          displayEventTime={false}
          eventClick={eventClick}
          startWeekDay={props.startWeekday ?? props.selectedStartDate.weekday}
          initialDate={props.selectedStartDate.toJSDate()}
          eventSources={[{ events }]}
          eventContent={(args: any, createElement: any) => {
            const reasons = args.event._def.extendedProps.reasons;
            let title = args.event._def.title;
            if (Array.isArray(reasons)) {
              return (
                <div>
                  <div className="fc-event-title">{title}</div>
                  <BlockReasonIcon
                    name="information-outline"
                    color="inherit"
                    className="blockReasonIcon"
                  />
                </div>
              );
            } else {
              const capacityText = args.event._def.extendedProps.capacityText;
              if (capacityText) {
                title += `<div class="capacity">${capacityText}</div>`;
              }
              return { html: `<div class="fc-event-title">${title}</div>` };
            }
          }}
          eventOrderWithoutId
          // 週表示設定
          expandRows
          slotEventOverlap={false}
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          dayHeaderContent={dayHeaderContent}
          eventMinHeight={16}
          styleProps={{
            withoutOutline: true,
            withoutContainerBorder: true,
          }}
          displayWeekdays={props.displayWeekdays}
        />
      )}
      <CalendarUnavailableButtonLayout
        ref={calendarUnavailableButtonRef}
        isShowUnavailableButton={isShowUnavailableButton}
      >
        <CalendarUnavailableButton
          color={props.color}
          isWeeklyView={true}
          onClick={props.onClickCalendarUnavailableButton}
        />
      </CalendarUnavailableButtonLayout>
    </TimeList>
  );
});

export default Component;

const CalendarUnavailableButtonLayout = styled.div<{
  isShowUnavailableButton: boolean;
}>`
  z-index: ${zIndex(1300)};
  position: absolute;
  left: calc(50% - 125.5px);
  top: calc(50% - 44px);
  ${({ isShowUnavailableButton }) => css`
    visibility: ${isShowUnavailableButton ? 'visible' : 'hidden'};
  `}
`;

const TimeList = styled.div<{
  color: string;
  isLightColor: boolean;
  numberOfVisibleHours: number;
  isShowUnavailableButton: boolean;
  unavailableTimeListHeight: number;
}>`
  position: relative;
  margin: ${spacing(16)} 0 0;
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(6)};
  overflow: hidden;

  ${mediaMax.tablet} {
    margin: ${spacing(16)} ${spacing(4)} 0;
  }

  // fullcalendar

  ${(props) => css`
    ${props.isShowUnavailableButton && 'overflow: hidden;'}

    height: ${
      props.isShowUnavailableButton
        ? props.unavailableTimeListHeight
        : 150 + props.numberOfVisibleHours * 115
    }px;

    ${mediaMax.tablet} {
      height: ${
        props.isShowUnavailableButton
          ? props.unavailableTimeListHeight
          : 100 + props.numberOfVisibleHours * 115
      }px;
    }

    & .fc .fc-col-header {
      background-color: ${white(props)};
      border-radius: ${borderRadius(6)};
    }
    & .fc .fc-scrollgrid,
    & .fc .fc-scrollgrid-section > td {
      background-color: initial;
    }
    & .fc td.fc-timegrid-divider {
      background-color: ${gray04(props)};
    }

    // 現在時刻
    & .fc .fc-timegrid-now-indicator-arrow {
      color: ${props.color};
    }
    & .fc .fc-timegrid-now-indicator-line {
      border-color: ${props.color};
      &::before {
        background-color: ${props.color};
      }
    }

    // 予定系
    & .fc .fc-event-main {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      text-align: left;
      padding: ${spacing(4)};
    }

    & .fc .fc-timegrid .fc-timegrid-event {
      ${mediaMax.tablet} {
        margin-right: 2px;
      }
    }

    // 選択可能予定
    & .fc .fc-timegrid-event.selectable-event {
      background-color: ${white(props)};
      border: 1px solid ${props.color};
      opacity: initial;
    }
    & .fc .fc-timegrid-event.selectable-event .fc-event-title {
      color: ${props.color};
      word-break: break-all;
      white-space: pre-wrap;
      font-size: ${fontSize(11)};
      line-height: ${fontSize(11)};

      ${mediaMax.tablet} {
        font-size: ${fontSize(10)};
        line-height: ${fontSize(10)};
      }
      .capacity {
        font-size: ${fontSize(10)};
        line-height: ${fontSize(10)};
      }
    }

    // ブロック
    & .fc .fc-timegrid-event.block-event {
      background-color: ${gray06(props)};
      border: 2px dashed ${gray02(props)};
      opacity: initial;
    }
    & .fc .fc-timegrid-event.block-event .fc-event-title {
      color: ${gray02(props)};
    }
    & .fc .fc-timegrid-event.block-event .blockReasonIcon {
      fill: ${gray02(props)};
    }
    & .fc .fc-timegrid-event.block-event:hover .blockReasonIcon {
      fill: ${props.isLightColor ? black(props) : white(props)};
    }

    // 満席
    & .fc .fc-timegrid-event.full-event {
      background-color: ${gray06(props)};
      border: 1px solid ${gray02(props)};
      opacity: initial;
      pointer-events: none;
    }
    & .fc .fc-timegrid-event.full-event .fc-event-title {
      color: ${gray02(props)};
    }
    & .fc .fc-timegrid-event.full-event .capacity {
      color: ${black(props)} !important;
    }

    // 予定なし予定
    & .fc .fc-timegrid-event.transparent-event {
      background-color: ${white(props)};
      border: 1px solid ${props.color};
      opacity: initial;
    }
    & .fc .fc-timegrid-event.transparent-event .fc-event-title {
      color: ${props.color};
    }
    & .fc .fc-timegrid-event.transparent-event .blockReasonIcon {
      fill: ${props.color};
    }
    & .fc .fc-timegrid-event.transparent-event:hover .blockReasonIcon {
      fill: ${props.isLightColor ? black(props) : white(props)};
    }

    // 選択した予定
    & .fc .fc-timegrid-event.selectable-event:hover,
    & .fc .fc-timegrid-event.selected-event {
      background-color: ${props.color};
      border: none;
      opacity: initial;
      font-size: ${fontSize(11)};
      line-height: ${fontSize(11)};

      .fc-event-title {
        color: ${props.isLightColor ? black(props) : white(props)} !important;
      }
      .capacity {
        color: ${props.isLightColor ? black(props) : white(props)} !important;
        font-size: ${fontSize(10)};
        line-height: ${fontSize(10)};
      }
    }
    & .fc .fc-timegrid-event.selectable-event .fc-event-title:hover,
    & .fc .fc-timegrid-event.selected-event .fc-event-title {
      color: ${props.isLightColor ? black(props) : white(props)};
      word-break: break-all;
      white-space: pre;
      font-size: ${fontSize(11)};
      line-height: ${fontSize(11)};

      .capacity {
        color: ${props.isLightColor ? black(props) : white(props)} !important;
        font-size: ${fontSize(10)};
        line-height: ${fontSize(10)};
      }

      ${mediaMax.tablet} {
        font-size: ${fontSize(10)};
        line-height: ${fontSize(10)};
      }
    }
  `}
`;

const WeekDayHeaderDate = styled.div<{
  today: boolean;
  color: string;
  isLightColor: boolean;
}>`
  width: 40px;
  height: 40px;
  margin-bottom: ${spacing(8)};
  font-size: ${fontSize(18)};
  font-weight: ${fontWeight('bold')};
  line-height: 40px;
  vertical-align: middle;
  border-radius: ${({ today }) => (today ? '20px' : 'initial')};
  background-color: ${({ today, color }) => (today ? color : 'initial')};
  color: ${({ today, isLightColor }) =>
    today ? (isLightColor ? black : whiteFix) : 'inherit'};

  ${mediaMax.tablet} {
    width: 20px;
    height: 20px;
    font-size: ${fontSize(10)};
    line-height: 20px;
    border-radius: ${({ today }) => (today ? '10px' : 'initial')};
  }
`;
const WeekDayHeaderWeekday = styled.div`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    font-size: ${fontSize(10)};
  }
`;

const LoadingHeader = styled.div`
  display: flex;
  height: 115.5px;
  border-bottom: 1px solid ${gray04};

  ${mediaMax.tablet} {
    height: 68.5px;
  }
`;
const LoadingAllday = styled.div`
  display: flex;
  height: 33px;
  border-bottom: 2px solid ${gray04};

  ${mediaMax.tablet} {
    height: 29px;
  }
`;
const LoadingBody = styled.div<{ hideBorderBottom?: boolean }>`
  display: flex;
  height: 144px;

  ${(props) =>
    !props.hideBorderBottom &&
    css`
      border-bottom: 1px solid ${gray04(props)};
    `}
`;
const LoadingAxis = styled.div`
  width: 43px;
  padding: ${spacing(4)};
  border-right: 1px solid ${gray04};
  ${mediaMax.tablet} {
    width: 37px;
  }
`;
const LoadingCell = styled.div<{ showBorderRight?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing(4)};

  ${(props) =>
    props.showBorderRight &&
    css`
      border-right: 1px solid ${gray04(props)};
    `}
`;

const BlockReasonIcon = styled(Icon)<{ isSelected?: boolean }>`
  width: 16px !important;
  height: 16px !important;
  margin-right: ${spacing(4)};
  flex-shrink: 0;

  ${mediaMax.smartphone} {
    width: 12px !important;
    height: 12px !important;
  }
`;
